import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FileBarChart, FileSpreadsheet, RefreshCw, Loader2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { fmtINR, formatErr, notifyErr, inputCls } from '../../../components/admin/billing/shared';

const GstReturnsPage = () => {
  const { authHeader } = useAuth();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const fetchSummary = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const { data } = await axios.get(`${API}/gst/returns/gstr3b/summary?year=${year}&month=${month}`, { headers: authHeader() });
      setSummary(data);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader, year, month]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const dlGstr1 = async () => {
    try {
      const resp = await axios.get(`${API}/gst/returns/gstr1/excel?year=${year}&month=${month}`, { headers: authHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `GSTR1_${year}-${String(month).padStart(2, '0')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) { notifyErr(e); }
  };

  return (
    <div className="space-y-4 p-4 sm:p-6 bg-slate-50 min-h-full" data-testid="gst-returns-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileBarChart className="text-teal-600" size={22} />
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">GST Returns</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className={inputCls} data-testid="gstr-year">
            {Array.from({ length: 5 }).map((_, i) => {
              const y = today.getFullYear() - 2 + i;
              return <option key={y} value={y}>{y}</option>;
            })}
          </select>
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className={inputCls} data-testid="gstr-month">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString('default', { month: 'long' })}</option>
            ))}
          </select>
          <button onClick={fetchSummary} className="text-sm bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 px-3 py-2 rounded-lg inline-flex items-center gap-1.5"><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>

      {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{err}</div>}

      {/* GSTR-1 Export card */}
      <div className="bg-gradient-to-br from-teal-500 to-teal-600 text-white rounded-2xl p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-xl font-bold">GSTR-1 — Outward Supplies</h2>
            <p className="text-sm opacity-90 mt-1">Excel workbook with <b>b2b</b>, <b>b2cl</b>, <b>b2cs</b>, and <b>hsn</b> sheets — upload format compatible with the GSTN offline tool.</p>
          </div>
          <button onClick={dlGstr1} className="bg-white text-teal-700 hover:bg-slate-50 font-bold px-5 py-3 rounded-lg inline-flex items-center gap-2 shadow-md" data-testid="gstr1-export-btn">
            <FileSpreadsheet size={18} /> Download GSTR-1 (xlsx)
          </button>
        </div>
      </div>

      {/* GSTR-3B Summary */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-bold text-slate-800">GSTR-3B Summary — {summary?.month_label || `${year}-${String(month).padStart(2, '0')}`}</h2>
            <p className="text-[11px] text-slate-500">Use these values when filing GSTR-3B at gst.gov.in</p>
          </div>
          {loading && <Loader2 className="animate-spin text-slate-400" size={16} />}
        </div>
        {summary && (
          <div className="p-5 grid sm:grid-cols-3 gap-3">
            {[
              { k: 'Invoice count', v: summary.invoice_count, mono: false },
              { k: 'B2B count', v: summary.b2b_count, mono: false },
              { k: 'B2C count', v: summary.b2c_count, mono: false },
              { k: '3.1(a) Outward taxable supplies', v: fmtINR(summary.outward_taxable_supplies), color: 'text-slate-900' },
              { k: 'CGST payable', v: fmtINR(summary.cgst_payable), color: 'text-amber-700' },
              { k: 'SGST payable', v: fmtINR(summary.sgst_payable), color: 'text-amber-700' },
              { k: 'IGST payable', v: fmtINR(summary.igst_payable), color: 'text-amber-700' },
              { k: 'Cess payable', v: fmtINR(summary.cess_payable), color: 'text-slate-600' },
              { k: 'Total tax payable', v: fmtINR(summary.total_tax_payable), color: 'text-red-700' },
            ].map((row) => (
              <div key={row.k} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{row.k}</div>
                <div className={`text-lg font-bold ${row.color || 'text-slate-900'} ${row.mono === false ? '' : 'font-mono'}`} data-testid={`gstr3b-${row.k.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{row.v}</div>
              </div>
            ))}
            <div className="sm:col-span-3 bg-teal-50 border-2 border-teal-300 rounded-lg p-4 flex justify-between items-center">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-teal-700 font-semibold">Grand Total (invoices)</div>
                <p className="text-[11px] text-slate-500">Sum of taxable value + all tax components</p>
              </div>
              <div className="text-2xl font-bold font-mono text-teal-700">{fmtINR(summary.grand_total)}</div>
            </div>
          </div>
        )}
      </div>

      <p className="text-xs text-slate-500 text-center">
        Only invoices with status <b>Issued</b> or <b>Paid</b> are included. Drafts and cancelled invoices are excluded.
      </p>
    </div>
  );
};

export default GstReturnsPage;
